/**
 * Layout for the SQL formatter: where lines break and how deep they are indented.
 *
 * Clauses (SELECT, FROM, WHERE, JOIN …) start lines. A clause with one item stays on its
 * line (`FROM users u`); a clause with a comma-separated list or several AND/OR
 * conditions puts one item per line beneath it. A bracket that opens a subquery breaks
 * across lines; any other bracket stays on its line unless it would not fit. Only
 * whitespace changes: every token comes out in the order it went in.
 */

import {
  CONDITION_CLAUSES,
  LIST_CLAUSES,
  SELECT_MODIFIERS,
} from "@/lib/tool-kit/latex-dev/sql-keywords";
import type { SqlTokenType } from "@/lib/tool-kit/latex-dev/sql-tokenizer";
import type { Group, Leaf, Node } from "@/lib/tool-kit/latex-dev/sql-tree";

export interface LayoutContext {
  /** One level of indentation: two spaces, four spaces or a tab. */
  unit: string;
  /** Lines longer than this are broken where a bracket allows. */
  maxWidth: number;
  /** Put the comma at the start of the next line. */
  commaFirst: boolean;
}

interface Atom {
  text: string;
  type: SqlTokenType;
  attach?: boolean;
  unary?: boolean;
}

interface PrintState {
  lines: string[];
  cur: string;
  indent: number;
  prev: Atom | null;
}

interface Part {
  nodes: Node[];
  /** Comments that followed the comma on the same line. */
  trailing: Leaf[];
}

const RE_TAB = /\t/g;

function pad(ctx: LayoutContext, level: number): string {
  return ctx.unit.repeat(Math.max(0, level));
}

function width(text: string): number {
  return text.replace(RE_TAB, "    ").length;
}

// ---------------------------------------------------------------------------
// Atoms and spacing
// ---------------------------------------------------------------------------

function leafAtom(node: Leaf): Atom {
  return { text: node.out, type: node.tok.type, unary: node.unary };
}

function openAtom(group: Group): Atom {
  if (group.shape === "case") {
    return { text: group.open.out, type: "word" };
  }
  return {
    text: group.open.tok.text,
    type: group.open.tok.type,
    attach: group.attach,
  };
}

function closeAtom(group: Group): Atom | null {
  if (!group.close) {
    return null;
  }
  return group.shape === "case"
    ? { text: group.close.out, type: "word" }
    : { text: group.close.tok.text, type: group.close.tok.type };
}

function atomsOf(nodes: Node[]): Atom[] {
  const out: Atom[] = [];
  for (const node of nodes) {
    if (node.kind === "tok") {
      out.push(leafAtom(node));
    } else {
      out.push(openAtom(node), ...atomsOf(node.items));
      const close = closeAtom(node);
      if (close) {
        out.push(close);
      }
    }
  }
  return out;
}

const NO_SPACE_BEFORE: ReadonlySet<SqlTokenType> = new Set([
  "close",
  "comma",
  "semicolon",
  "dot",
  "bracketClose",
]);

const NO_SPACE_AFTER: ReadonlySet<SqlTokenType> = new Set([
  "open",
  "dot",
  "bracketOpen",
]);

function needSpace(prev: Atom | null, next: Atom): boolean {
  if (!prev) {
    return false;
  }
  if (NO_SPACE_BEFORE.has(next.type) || NO_SPACE_AFTER.has(prev.type)) {
    return false;
  }
  if (next.type === "open" || next.type === "bracketOpen") {
    return !next.attach;
  }
  if (next.text === "::" || prev.text === "::") {
    return false;
  }
  return !prev.unary;
}

/** The atoms joined on one line. */
function joinAtoms(atoms: Atom[]): string {
  let out = "";
  let prev: Atom | null = null;
  for (const atom of atoms) {
    out += (needSpace(prev, atom) ? " " : "") + atom.text;
    prev = atom;
  }
  return out;
}

function hasLineComment(nodes: Node[]): boolean {
  return nodes.some((n) =>
    n.kind === "tok" ? n.tok.type === "lineComment" : hasLineComment(n.items)
  );
}

function isComment(node: Node | undefined): node is Leaf {
  return (
    node?.kind === "tok" &&
    (node.tok.type === "lineComment" || node.tok.type === "blockComment")
  );
}

function isWord(node: Node | undefined, upper: string): node is Leaf {
  return (
    node?.kind === "tok" && node.tok.type === "word" && node.upper === upper
  );
}

// ---------------------------------------------------------------------------
// Splitting
// ---------------------------------------------------------------------------

/** Split at top-level commas. A comment on the comma's line stays with the item before it. */
function splitCommas(nodes: Node[]): Part[] {
  const parts: Part[] = [];
  let current: Node[] = [];
  let afterComma = false;
  for (const node of nodes) {
    if (node.kind === "tok" && node.tok.type === "comma") {
      parts.push({ nodes: current, trailing: [] });
      current = [];
      afterComma = true;
    } else if (afterComma && isComment(node) && node.tok.nl === 0) {
      parts.at(-1)?.trailing.push(node);
    } else {
      afterComma = false;
      current.push(node);
    }
  }
  parts.push({ nodes: current, trailing: [] });
  return parts;
}

/** Split before each top-level AND / OR, except the AND that belongs to a BETWEEN. */
function splitLogic(nodes: Node[]): Part[] {
  const parts: Part[] = [];
  let current: Node[] = [];
  let between = 0;
  for (const node of nodes) {
    if (isWord(node, "BETWEEN")) {
      between++;
    }
    const isAnd = isWord(node, "AND");
    if (isAnd && between > 0) {
      between--;
    } else if ((isAnd || isWord(node, "OR")) && current.length > 0) {
      parts.push({ nodes: current, trailing: [] });
      current = [];
    }
    current.push(node);
  }
  parts.push({ nodes: current, trailing: [] });
  return parts;
}

// ---------------------------------------------------------------------------
// Printing one item
// ---------------------------------------------------------------------------

function emit(st: PrintState, atom: Atom): void {
  st.cur += (needSpace(st.prev, atom) ? " " : "") + atom.text;
  st.prev = atom;
}

function breakLine(st: PrintState, ctx: LayoutContext, level: number): void {
  st.lines.push(st.cur);
  st.cur = pad(ctx, level);
  st.indent = level;
  st.prev = null;
}

function feedComment(
  st: PrintState,
  node: Leaf,
  ctx: LayoutContext,
  cont: number
): void {
  const ownLine = node.tok.nl > 0;
  if (ownLine && st.cur.trim() !== "") {
    breakLine(st, ctx, cont);
  }
  const space = st.cur.trim() === "" || st.prev === null ? "" : " ";
  st.cur += space + node.tok.text;
  st.prev = { text: node.tok.text, type: node.tok.type };
  if (node.tok.type === "lineComment") {
    breakLine(st, ctx, cont);
  }
}

function startsBranch(node: Node): boolean {
  return isWord(node, "WHEN") || isWord(node, "ELSE");
}

function caseBranches(items: Node[]): { operand: Node[]; branches: Node[][] } {
  const operand: Node[] = [];
  const branches: Node[][] = [];
  for (const node of items) {
    if (startsBranch(node)) {
      branches.push([node]);
    } else if (branches.length > 0) {
      branches.at(-1)?.push(node);
    } else {
      operand.push(node);
    }
  }
  return { operand, branches };
}

function feedCase(
  st: PrintState,
  group: Group,
  ctx: LayoutContext,
  cont: number
): void {
  const atoms = atomsOf([group]);
  const flat = joinAtoms(atoms);
  const { operand, branches } = caseBranches(group.items);
  const whens = branches.filter((b) => isWord(b[0], "WHEN")).length;
  const fits = width(st.cur) + flat.length + 1 <= ctx.maxWidth;
  if (!hasLineComment(group.items) && whens <= 1 && fits) {
    for (const atom of atoms) {
      emit(st, atom);
    }
    return;
  }
  emit(st, openAtom(group));
  for (const node of operand) {
    feedNode(st, node, ctx, cont);
  }
  st.lines.push(st.cur);
  const level = st.indent + 1;
  st.lines.push(
    ...branches.flatMap((nodes) =>
      layoutItem(nodes, pad(ctx, level), level, level + 1, ctx)
    )
  );
  closeOn(st, group, ctx, level - 1);
}

/** Put the closing token of a multi-line group on its own line at `level`. */
function closeOn(
  st: PrintState,
  group: Group,
  ctx: LayoutContext,
  level: number
): void {
  const close = closeAtom(group);
  st.cur = pad(ctx, level) + (close ? close.text : "");
  st.indent = level;
  st.prev = close;
}

function splitInline(group: Group): { parts: Part[]; byLogic: boolean } {
  const commas = splitCommas(group.items);
  if (commas.length > 1) {
    return { parts: commas, byLogic: false };
  }
  return { parts: splitLogic(group.items), byLogic: true };
}

function feedExpanded(st: PrintState, group: Group, ctx: LayoutContext): void {
  emit(st, openAtom(group));
  st.lines.push(st.cur);
  const level = st.indent + 1;
  const { parts, byLogic } = splitInline(group);
  st.lines.push(
    ...renderParts(parts, ctx, {
      first: pad(ctx, level),
      rest: pad(ctx, level) + (ctx.commaFirst && !byLogic ? ", " : ""),
      level,
      commas: !byLogic,
    })
  );
  closeOn(st, group, ctx, level - 1);
}

function feedBlock(st: PrintState, group: Group, ctx: LayoutContext): void {
  emit(st, openAtom(group));
  st.lines.push(st.cur);
  const level = st.indent + 1;
  st.lines.push(...layoutBlock(group.items, level, ctx));
  closeOn(st, group, ctx, level - 1);
}

function canExpand(group: Group): boolean {
  const { parts } = splitInline(group);
  return parts.length > 1;
}

function feedInline(st: PrintState, group: Group, ctx: LayoutContext): void {
  const atoms = atomsOf([group]);
  const flat = joinAtoms(atoms);
  const projected = width(st.cur) + 1 + flat.length;
  const mustBreak = hasLineComment(group.items);
  if (
    group.role === "ddl" ||
    mustBreak ||
    (projected > ctx.maxWidth && canExpand(group))
  ) {
    feedExpanded(st, group, ctx);
    return;
  }
  for (const atom of atoms) {
    emit(st, atom);
  }
}

function feedNode(
  st: PrintState,
  node: Node,
  ctx: LayoutContext,
  cont: number
): void {
  if (node.kind === "tok") {
    if (isComment(node)) {
      feedComment(st, node, ctx, cont);
    } else {
      emit(st, leafAtom(node));
    }
    return;
  }
  if (node.role === "block") {
    feedBlock(st, node, ctx);
  } else if (node.role === "case") {
    feedCase(st, node, ctx, cont);
  } else {
    feedInline(st, node, ctx);
  }
}

/**
 * The lines for one item. `prefix` is what the first line already holds (indentation and
 * perhaps a clause word), `indent` the level of that line, `cont` the level of
 * continuation lines after a comment.
 */
function layoutItem(
  nodes: Node[],
  prefix: string,
  indent: number,
  cont: number,
  ctx: LayoutContext
): string[] {
  const st: PrintState = { lines: [], cur: prefix, indent, prev: null };
  for (const node of nodes) {
    feedNode(st, node, ctx, cont);
  }
  if (st.cur.trim() !== "") {
    st.lines.push(st.cur);
  }
  return st.lines;
}

// ---------------------------------------------------------------------------
// Lists of items
// ---------------------------------------------------------------------------

interface PartStyle {
  first: string;
  rest: string;
  level: number;
  commas: boolean;
}

function lastCommentText(nodes: Node[]): string | null {
  const last = nodes.at(-1);
  return last?.kind === "tok" && last.tok.type === "lineComment"
    ? last.tok.text
    : null;
}

/** Add the separating comma to an item's last line, ahead of any trailing line comment. */
function addComma(lines: string[], comment: string | null): void {
  const index = lines.length - 1;
  const line = lines[index];
  lines[index] =
    comment !== null && line.endsWith(comment)
      ? `${line.slice(0, line.length - comment.length).trimEnd()}, ${comment}`
      : `${line},`;
}

function renderParts(
  parts: Part[],
  ctx: LayoutContext,
  style: PartStyle
): string[] {
  const out: string[] = [];
  const lastIndex = parts.length - 1;
  parts.forEach((part, k) => {
    const prefix = k === 0 ? style.first : style.rest;
    const lines = layoutItem(
      part.nodes,
      prefix,
      style.level,
      style.level + 1,
      ctx
    );
    if (lines.length === 0) {
      return;
    }
    if (style.commas && !ctx.commaFirst && k < lastIndex) {
      addComma(lines, lastCommentText(part.nodes));
    }
    if (part.trailing.length > 0) {
      const tail = part.trailing.map((c) => c.tok.text).join(" ");
      lines[lines.length - 1] += ` ${tail}`;
    }
    out.push(...lines);
  });
  return out;
}

// ---------------------------------------------------------------------------
// Clauses
// ---------------------------------------------------------------------------

/** Words that stay on the SELECT line: DISTINCT, DISTINCT ON (…), TOP n. */
function takeSelectModifiers(body: Node[]): { mods: Node[]; rest: Node[] } {
  let i = 0;
  const first = body[i];
  const isMod = (node: Node | undefined): node is Leaf =>
    node?.kind === "tok" && SELECT_MODIFIERS.has(node.upper);
  while (isMod(body[i])) {
    i++;
  }
  if (i > 0 && isWord(body[i], "ON") && body[i + 1]?.kind === "group") {
    i += 2;
  }
  if (i === 0 && isWord(first, "TOP") && first.role === "keyword") {
    i = 1;
    const operand = body[i];
    const sized = operand?.kind === "group" || operand?.kind === "tok";
    i += sized ? 1 : 0;
    if (isWord(body[i], "PERCENT")) {
      i++;
    }
  }
  return { mods: body.slice(0, i), rest: body.slice(i) };
}

function isClause(node: Node | undefined): node is Leaf {
  return node?.kind === "tok" && node.role === "clause";
}

function layoutCompact(
  head: string,
  body: Node[],
  indent: number,
  ctx: LayoutContext
): string[] {
  const prefix = `${pad(ctx, indent)}${head}`;
  if (body.length === 0) {
    return [prefix];
  }
  return layoutItem(body, `${prefix} `, indent, indent + 1, ctx);
}

function layoutBroken(
  head: string,
  parts: Part[],
  indent: number,
  ctx: LayoutContext,
  commas: boolean
): string[] {
  const level = indent + 1;
  return [
    `${pad(ctx, indent)}${head}`,
    ...renderParts(parts, ctx, {
      first: pad(ctx, level),
      rest: pad(ctx, level) + (commas && ctx.commaFirst ? ", " : ""),
      level,
      commas,
    }),
  ];
}

function layoutWith(
  head: string,
  body: Node[],
  indent: number,
  ctx: LayoutContext
): string[] {
  return renderParts(splitCommas(body), ctx, {
    first: `${pad(ctx, indent)}${head} `,
    rest: pad(ctx, indent) + (ctx.commaFirst ? ", " : ""),
    level: indent,
    commas: true,
  });
}

function layoutJoin(
  head: Leaf,
  body: Node[],
  indent: number,
  ctx: LayoutContext
): string[] {
  const onIndex = body.findIndex((n) => isWord(n, "ON"));
  const prefix = `${pad(ctx, indent)}${head.out} `;
  if (onIndex < 0) {
    return layoutItem(body, prefix, indent, indent + 1, ctx);
  }
  const table = body.slice(0, onIndex);
  const condition = body.slice(onIndex + 1);
  const parts = splitLogic(condition);
  if (parts.length === 1) {
    const single = layoutItem(body, prefix, indent, indent + 1, ctx);
    if (single.length === 1 && width(single[0]) <= ctx.maxWidth) {
      return single;
    }
  }
  const on = body[onIndex] as Leaf;
  const level = indent + 1;
  const lines = layoutItem(table, prefix, indent, indent + 1, ctx);
  parts.forEach((part, k) => {
    const lead = k === 0 ? `${pad(ctx, level)}${on.out} ` : pad(ctx, level);
    lines.push(...layoutItem(part.nodes, lead, level, level + 1, ctx));
  });
  return lines;
}

function layoutClause(
  head: Leaf,
  body0: Node[],
  indent: number,
  ctx: LayoutContext
): string[] {
  let body = body0;
  let headText = head.out;
  if (head.key === "SELECT") {
    const { mods, rest } = takeSelectModifiers(body);
    if (mods.length > 0) {
      headText = `${headText} ${joinAtoms(atomsOf(mods))}`;
      body = rest;
    }
  }
  const first = body[0];
  let forceBreak = false;
  if (isComment(first) && first.tok.nl === 0) {
    headText = `${headText} ${first.tok.text}`;
    body = body.slice(1);
    forceBreak = first.tok.type === "lineComment";
  }
  return layoutByKind(head, headText, body, indent, ctx, forceBreak);
}

function layoutByKind(
  head: Leaf,
  headText: string,
  body: Node[],
  indent: number,
  ctx: LayoutContext,
  forceBreak: boolean
): string[] {
  const key = head.key;
  if (key === "WITH" || key === "WITH RECURSIVE") {
    return layoutWith(headText, body, indent, ctx);
  }
  if (key === "JOIN") {
    return layoutJoin(head, body, indent, ctx);
  }
  const isList = LIST_CLAUSES.has(key);
  const isCondition = CONDITION_CLAUSES.has(key);
  const parts = splitFor(isList, isCondition, body);
  if (parts.length > 1 || (forceBreak && body.length > 0)) {
    return layoutBroken(headText, parts, indent, ctx, isList);
  }
  const compact = layoutCompact(headText, body, indent, ctx);
  // A column that needs several lines (CASE, a subquery) gets the lines to itself.
  if (key === "SELECT" && compact.length > 1) {
    return layoutBroken(headText, parts, indent, ctx, isList);
  }
  return compact;
}

function splitFor(isList: boolean, isCondition: boolean, body: Node[]): Part[] {
  if (isList) {
    return splitCommas(body);
  }
  return isCondition ? splitLogic(body) : [{ nodes: body, trailing: [] }];
}

function isOwnLineComment(node: Node | undefined): boolean {
  return isComment(node) && node.tok.nl > 0;
}

/** The lines of a run of clauses: a statement, or the inside of a subquery. */
export function layoutBlock(
  nodes: Node[],
  indent: number,
  ctx: LayoutContext
): string[] {
  const lines: string[] = [];
  let i = 0;
  while (i < nodes.length) {
    const node = nodes[i];
    let end = i + 1;
    while (end < nodes.length && !isClause(nodes[end])) {
      end++;
    }
    if (isClause(node)) {
      // Comments on their own lines belong to the clause that follows them.
      let bodyEnd = end;
      while (bodyEnd > i + 1 && isOwnLineComment(nodes[bodyEnd - 1])) {
        bodyEnd--;
      }
      lines.push(
        ...layoutClause(node, nodes.slice(i + 1, bodyEnd), indent, ctx)
      );
      i = bodyEnd;
    } else {
      lines.push(
        ...layoutItem(
          nodes.slice(i, end),
          pad(ctx, indent),
          indent,
          indent,
          ctx
        )
      );
      i = end;
    }
  }
  return lines;
}
